import { zodResolver } from '@hookform/resolvers/zod'
import {
	type PostAuthSignUpWithEmailData,
	postAuthSignUpWithEmail
} from '@repo/api-portal-sdk/gen'
import { Button } from '@repo/ui/button'
import { Field, FieldGroup } from '@repo/ui/field'
import { AuthErrorAlert } from '@repo/ui/modules/auth/components/auth-error-alert'
import { Spinner } from '@repo/ui/spinner'
import { useMutation } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import type { z } from 'zod'
import { resolveAppLanguageCode, useTranslation } from '@/lib/i18n'
import { createSignupSchema } from '@/routes/-lib/auth-schemas'
import { AuthTextField } from './auth-text-field'
import type { SignupOtpState } from './signup-otp-form'

export function SignupForm(props: {
	onRequiresOtp: (state: SignupOtpState) => void
	onSuccess: () => Promise<void>
}) {
	const { t, i18n } = useTranslation()
	const language = resolveAppLanguageCode(
		i18n.resolvedLanguage ?? i18n.language
	)
	const schema = createSignupSchema(t)
	const form = useForm<z.infer<ReturnType<typeof createSignupSchema>>>({
		resolver: zodResolver(schema),
		defaultValues: { email: '', password: '', confirmPassword: '' }
	})
	const signUp = useMutation({
		mutationFn: (body: PostAuthSignUpWithEmailData['body']) =>
			postAuthSignUpWithEmail({ body }),
		onError: () => {
			form.resetField('password')
			form.resetField('confirmPassword')
		},
		onSuccess: async (response, body) => {
			if (response.data.status === 'requires_otp') {
				props.onRequiresOtp({
					email: body.email,
					emailOtpToken: response.data.emailOtpToken
				})
				return
			}
			await props.onSuccess()
		}
	})

	return (
		<form
			onSubmit={form.handleSubmit((data) =>
				signUp.mutate({
					email: data.email,
					password: data.password,
					rememberMe: false,
					language
				})
			)}
		>
			<FieldGroup className='gap-4'>
				{signUp.isError ? (
					<AuthErrorAlert
						error={signUp.error}
						onDismiss={signUp.reset}
					/>
				) : null}
				<AuthTextField
					autoComplete='email'
					control={form.control}
					label={t('common:auth.email')}
					name='email'
					placeholder={t('common:auth.email_placeholder')}
					type='email'
				/>
				<AuthTextField
					autoComplete='new-password'
					control={form.control}
					description={t('common:auth.password_min')}
					label={t('common:auth.password')}
					name='password'
					type='password'
				/>
				<AuthTextField
					autoComplete='new-password'
					control={form.control}
					label={t('common:auth.confirm_password')}
					name='confirmPassword'
					type='password'
				/>
				<Field>
					<Button disabled={signUp.isPending} size='lg' type='submit'>
						{signUp.isPending ? (
							<Spinner />
						) : (
							t('common:auth.signup_submit')
						)}
					</Button>
				</Field>
				<p className='text-center text-muted-foreground text-xs'>
					{t('common:auth.legal_prefix')}{' '}
					<Link className='text-primary hover:underline' to='/terms'>
						{t('common:auth.legal_terms')}
					</Link>{' '}
					{t('common:auth.legal_and')}{' '}
					<Link
						className='text-primary hover:underline'
						to='/privacy'
					>
						{t('common:auth.legal_privacy')}
					</Link>
					.
				</p>
			</FieldGroup>
		</form>
	)
}
